import moneyHelper from '../../helpers/money';
import MatchMedia from '../../helpers/matchMedia';
import Amount from '../../helpers/amount';
import 'intersection-observer';
import 'web-animations-js';
import { EVENT_NAME_CLOSE_RECEIPT, PAY_PER_USE, VAT_SETTINGS, COUNTRY_NETHERLANDS, COUNTRY_BELGIUM } from '../../helpers/consts';

const cssClasses = {
    receipt: 'receipt',
};

const STICK_TO_TOP = 'top';
const STICK_TO_BOTTOM = 'bottom';

export class Receipt {
    /**
     * Component that handles the receipt
     * @constructor
     * @param {Object} options
     * @param {Store} options.store
     */
    constructor(options) {
        this.store = options.store;
        this.state = this.store.getState();
        this.translations = options.translations;

        this.updateVat();
        // All elements that get updated.
        this.receiptElements = {
            users: [],
            freeUsers: [],
            modules: [],
            extras: [],
        };

        this.$sidebar = document.querySelector('#sidebar');
        this.$wizardSteps = document.querySelector('.wizard__steps');
        this.$sidebarHeight = 0;
        this.$sidebarInner = this.$sidebar.querySelector('.sidebar__inner');
        this.$stickyTop = this.$sidebar.querySelector('.sticky--top');
        this.$stickyBottom = this.$sidebar.querySelector('.sticky--bottom');
        this.$wizardNavigation = document.querySelector('#wizardNavigation');
        this.headerIsVisible = false;
        this.footerIsVisible = false;
        this.$el = this.createReceiptElement();
        if (this.$wizardNavigation) this.$wizardNavigation.appendChild(this.createCompactReceipt());
        this.store.subscribe(this.update.bind(this));

        this.checkFixedReceipt();
        this.update();
    }

    checkFixedReceipt() {
        this.$sidebarHeight = this.$sidebar.offsetHeight;
        const observerFooter = new IntersectionObserver((records) => {
            records.forEach((entry) => {
                this.footerIsVisible = (entry.intersectionRatio > 0);
                this.updateSideBar();
            });
        });

        const observerHeader = new IntersectionObserver((records) => {
            records.forEach((entry) => {
                this.headerIsVisible = entry.intersectionRatio > 0;
                this.updateSideBar();
            });
        });

        const observerSidebar = new IntersectionObserver(() => {
            this.updateSideBar();
        });

        observerFooter.observe(this.$stickyBottom);
        observerHeader.observe(this.$stickyTop);
        observerSidebar.observe(this.$sidebarInner);
    }

    updateSideBar() {
        const sidebarFitsInViewport = (
            window.innerHeight > this.$sidebar.offsetHeight + 60 &&
            this.$wizardSteps.offsetHeight > this.$sidebar.offsetHeight + 60
        );

        if (sidebarFitsInViewport && !this.headerIsVisible && this.footerIsVisible) {
            this.animateSideBar(STICK_TO_TOP);
        } else if (!this.headerIsVisible && this.footerIsVisible) {
            this.animateSideBar(STICK_TO_BOTTOM);
        } else {
            this.animateSideBar();
        }

        this.$sidebarHeight = this.$sidebar.offsetHeight;
    }

    animateSideBar(stickTo = null) {
        if (MatchMedia.small()) {
            return;
        }

        // Get the first position.
        const first = this.$sidebarInner.getBoundingClientRect();
        this.$sidebar.classList.remove('sidebar--fixed');
        this.$sidebar.classList.remove('sidebar--fixed--top');
        this.$sidebar.classList.remove('sidebar--fixed--bottom');

        // Move it to the end.
        switch(stickTo) {
        case STICK_TO_TOP:
            this.$sidebar.classList.add('sidebar--fixed');
            this.$sidebar.classList.add('sidebar--fixed--top');
            break;
        case STICK_TO_BOTTOM:
            this.$sidebar.classList.add('sidebar--fixed');
            this.$sidebar.classList.add('sidebar--fixed--bottom');
            break;
        default:
            this.$sidebar.classList.remove('sidebar--fixed');
            this.$sidebar.classList.remove('sidebar--fixed--top');
            this.$sidebar.classList.remove('sidebar--fixed--bottom');
            break;
        }

        // Get the last position.
        const last = this.$sidebarInner.getBoundingClientRect();

        // Invert.
        const invert = first.top - last.top;

        // Go from the inverted position to last.
        if (this.$sidebarInner.animate && invert !== 0) {
            const player = this.$sidebarInner.animate([
                { transform: `translateY(${invert}px)` },
                { transform: 'translateY(0)' }
            ], {
                duration: 300,
                easing: 'cubic-bezier(0,0,0.32,1)',
            });

            // Do any tidy up at the end
            // of the animation.
            player.addEventListener('finish', () => {});
        }
    }

    createReceiptElement() {
        this.$receiptElem = document.createElement('div');
        this.$receiptElem.classList.add(cssClasses.receipt);

        this.$packageRow = this.createPackageRow(this.state.product.name, this.state.product.amount);
        this.$receiptElem.appendChild(this.$packageRow);

        for (const user of this.state.users) {
            if (user.numberOfFreeUsers && user.numberOfFreeUsers > 0) {
                this.$receiptElem.appendChild(this.createFreeUserRow(user));
            }
        }

        this.$extraUsersHeading = this.createHeading(__('Extra'));
        this.$receiptElem.appendChild(this.$extraUsersHeading);

        for (const user of this.state.users) {
            const userRow = this.createUserRow(user);

            if (userRow) {
                this.$receiptElem.appendChild(userRow);
            }
        }

        this.$modulesHeading = this.createHeading(this.translations.receipt_addons ? this.translations.receipt_addons : __('Add-ons'));
        this.$receiptElem.appendChild(this.$modulesHeading);

        for (const module of this.state.modules) {
            const moduleRow = this.createModuleRow(module);

            if (moduleRow) {
                this.$receiptElem.appendChild(moduleRow);
            }
        }

        this.$extrasHeading = this.createHeading(__('Services'));
        this.$receiptElem.appendChild(this.$extrasHeading);

        for (const extra of this.state.extras) {
            const extraRow = this.createExtraRow(extra);

            if (extraRow) {
                this.$receiptElem.appendChild(extraRow);
            }
        }

        this.$totalAmountRow = this.createTotalAmountRow();
        this.$receiptElem.appendChild(this.$totalAmountRow);
        this.$payOnceAmountRow = this.createTotalPayOnceAmountRow();
        this.$receiptElem.appendChild(this.$payOnceAmountRow);

        return this.$receiptElem;
    }

    createCompactReceipt() {
        const $buttonElem = document.createElement('button');
        const $buttonInnerElem = document.createElement('span');
        Object.defineProperty(this.receiptElements, '$buttonPriceElemText', {
            value: document.createTextNode(0),
            writable: false
        });

        $buttonElem.type = 'button';
        $buttonElem.classList.add('btn', 'btn--compact-receipt', 'hidden-md', 'hidden-lg');
        $buttonInnerElem.classList.add('btn__inner', 'icon--before', 'icon--arrow-up');
        $buttonInnerElem.appendChild(this.receiptElements.$buttonPriceElemText);
        $buttonElem.appendChild($buttonInnerElem);

        $buttonElem.addEventListener('click', () => {
            $buttonElem.classList.toggle('btn--active');
            $buttonInnerElem.classList.toggle('icon--arrow-up');
            $buttonInnerElem.classList.toggle('icon--close');

            if (! document.body.classList.contains('body--sidebar-active')) {
                this.scrollPosition = window.pageYOffset;
                document.body.classList.add('body--sidebar-active');
                document.body.style.top = `${- this.scrollPosition }px`;
            } else if (document.body.classList.contains('body--sidebar-active')) {
                document.body.classList.remove('body--sidebar-active');
                document.body.style.top = null;
                window.scrollTo(0, this.scrollPosition);
            }
        });

        document.addEventListener(EVENT_NAME_CLOSE_RECEIPT, () => {
            $buttonElem.classList.remove('btn--active');
            $buttonInnerElem.classList.add('icon--arrow-up');
            $buttonInnerElem.classList.remove('icon--close');

            document.body.classList.remove('body--sidebar-active');
            document.body.style.top = null;
            window.scrollTo(0, this.scrollPosition);
        });

        return $buttonElem;
    }

    createPackageRow(name = "", amount = 0) {
        const $rowElem = document.createElement('div');
        const $labelElem = document.createElement('span');
        const $priceElem = document.createElement('span');
        const $labelElemText = document.createTextNode(name);
        const discountPercentage = this.state.product.contractTermDiscountPercentage ? Number(this.state.product.contractTermDiscountPercentage) : 0;

        Object.defineProperty(this.receiptElements, '$priceElemText', {
            value: document.createTextNode(moneyHelper.formatPrice(amount / (1 - discountPercentage))),
            writable: false
        });

        $rowElem.classList.add('receipt__row', 'receipt__row--package');
        $labelElem.classList.add('receipt__col', 'receipt__col--left', 'text--bold', 'h5');
        $priceElem.classList.add('receipt__col', 'receipt__col--right', 'text-right', 'text--bold', 'h5');
        $labelElem.appendChild($labelElemText);
        $priceElem.appendChild(this.receiptElements.$priceElemText);

        $rowElem.appendChild($labelElem);
        $rowElem.appendChild($priceElem);

        return $rowElem;
    }

    createFreeUserRow(user) { // eslint-disable-line class-methods-use-this
        const { numberOfFreeUsers } = user;
        const $userRowElem = document.createElement('div');
        const $userLabelElem = document.createElement('span');
        const $userPriceElem = document.createElement('span');
        const $userLabelElemText = document.createTextNode(` x ${user.name}`);

        this.receiptElements.freeUsers.push({
            type: user.userType,
            $userPriceElemText: document.createTextNode(__('Incl.')),
            $numberOfUsersLabelElemText: document.createTextNode(numberOfFreeUsers)
        });

        $userRowElem.classList.add('receipt__row', 'receipt__row--users');
        $userLabelElem.classList.add('receipt__col', 'receipt__col--left');
        $userPriceElem.classList.add('receipt__col', 'receipt__col--right', 'text-right', 'receipt__col--free');

        $userLabelElem.appendChild(this.receiptElements.freeUsers[this.receiptElements.freeUsers.length - 1].$numberOfUsersLabelElemText);
        $userLabelElem.appendChild($userLabelElemText);
        $userPriceElem.appendChild(this.receiptElements.freeUsers[this.receiptElements.freeUsers.length - 1].$userPriceElemText);

        $userRowElem.appendChild($userLabelElem);
        $userRowElem.appendChild($userPriceElem);

        return $userRowElem;
    }

    createUserRow(user) { // eslint-disable-line class-methods-use-this
        const $parentElement = document.createElement('div');

        const makeRow = ({ type, amountPerUser, numberOfUsers, numberOfFreeUsers, name }) => {
            const $userRowElem = document.createElement('div');
            const $userLabelElem = document.createElement('span');
            const $userPriceElem = document.createElement('span');
            const $userLabelElemText = document.createTextNode(` x ${name}`);

            this.receiptElements.users.push({
                type,
                $userPriceElemText: document.createTextNode(moneyHelper.formatPrice(amountPerUser * (numberOfUsers - numberOfFreeUsers))),
                $numberOfUsersLabelElemText: document.createTextNode(numberOfUsers - numberOfFreeUsers),
                $userLabelElemText
            });

            $userRowElem.classList.add('receipt__row', 'receipt__row--users');
            $userLabelElem.classList.add('receipt__col', 'receipt__col--left');
            $userPriceElem.classList.add('receipt__col', 'receipt__col--right', 'text-right');
            $userRowElem.classList.toggle('receipt__row--disabled', (numberOfUsers <= numberOfFreeUsers));

            $userLabelElem.appendChild(this.receiptElements.users[this.receiptElements.users.length - 1].$numberOfUsersLabelElemText);
            $userLabelElem.appendChild(this.receiptElements.users[this.receiptElements.users.length - 1].$userLabelElemText);
            $userPriceElem.appendChild(this.receiptElements.users[this.receiptElements.users.length - 1].$userPriceElemText);

            if (type.indexOf('contentProviders') > -1) {
                $userRowElem.classList.add('visually-hidden');
            }

            $userRowElem.appendChild($userLabelElem);
            $userRowElem.appendChild($userPriceElem);

            $parentElement.appendChild($userRowElem);
        }

        makeRow({
            type: user.userType,
            name: user.name,
            amountPerUser: user.amountPerUser,
            numberOfUsers: user.numberOfFreeUsers
        });

        if (user.contentProviders.length > 0) {
            makeRow({
                type: `${user.userType}_contentProviders`,
                name: '',
                amountPerUser: user.contentProviders[0].amount,
                numberOfUsers: 0
            });
        }

        return $parentElement;
    }

    createHeading(headingText = "") { // eslint-disable-line class-methods-use-this
        const $extraContainerElem = document.createElement('div');
        const $rowElem = document.createElement('div');
        const $textElem = document.createElement('span');
        const $textElemText = document.createTextNode(headingText);

        $textElem.appendChild($textElemText)

        $rowElem.classList.add('receipt__row', 'receipt__row--heading', 'h5');
        $extraContainerElem.classList.add('receipt__extras');

        $rowElem.appendChild($textElem);
        $extraContainerElem.appendChild($rowElem);

        return $extraContainerElem;
    }

    createModuleRow(module) { // eslint-disable-line class-methods-use-this
        const { amount, active, name, numberOfItems, bundlePrice } = module;
        const $moduleRowElem = document.createElement('div');
        const $moduleLabelElem = document.createElement('span');
        const $modulePriceElem = document.createElement('span');
        const $moduleLabelElemText = document.createTextNode(name);

        let realAmount = amount;
        if (Number(numberOfItems) !== 0 && Number(bundlePrice) !== 0) {
            realAmount = this.calculateBundleItemsPrice(module);
        }

        this.receiptElements.modules.push({
            type: module.moduleType,
            $modulePriceElemText: document.createTextNode(Number(realAmount) !== 0 ? moneyHelper.formatPrice(realAmount) : PAY_PER_USE),
        });

        $moduleRowElem.classList.add('receipt__row', 'receipt__row--modules');
        $moduleLabelElem.classList.add('receipt__col', 'receipt__col--left');
        $modulePriceElem.classList.add('receipt__col', 'receipt__col--right', 'text-right');
        $modulePriceElem.classList.toggle('receipt__col--text', (Number(realAmount) === 0));
        $moduleRowElem.classList.toggle('receipt__row--disabled', !active);

        $moduleLabelElem.appendChild($moduleLabelElemText);
        $modulePriceElem.appendChild(this.receiptElements.modules[this.receiptElements.modules.length - 1].$modulePriceElemText);

        $moduleRowElem.appendChild($moduleLabelElem);
        $moduleRowElem.appendChild($modulePriceElem);

        return $moduleRowElem;
    }

    calculateBundleItemsPrice(module) { // eslint-disable-line class-methods-use-this
        let { numberOfItems, bundlePrice } = module;

        bundlePrice = parseFloat(bundlePrice);
        numberOfItems = parseFloat(numberOfItems);

        const bundleTotalPrice = numberOfItems * bundlePrice;
        return bundleTotalPrice;
    }

    createExtraRow(extra) { // eslint-disable-line class-methods-use-this
        const { amount, active, name } = extra;
        const $extraRowElem = document.createElement('div');
        const $extraLabelElem = document.createElement('span');
        const $extraPriceElem = document.createElement('span');
        const $extraLabelElemText = document.createTextNode(name);

        this.receiptElements.extras.push({
            type: extra.extraType,
            $extraPriceElemText: document.createTextNode(moneyHelper.formatPrice(amount)),
        });

        $extraRowElem.classList.add('receipt__row', 'receipt__row--extras');
        $extraLabelElem.classList.add('receipt__col', 'receipt__col--left');
        $extraPriceElem.classList.add('receipt__col', 'receipt__col--right', 'text-right');
        $extraRowElem.classList.toggle('receipt__row--disabled', !active);

        $extraLabelElem.appendChild($extraLabelElemText);
        $extraPriceElem.appendChild(this.receiptElements.extras[this.receiptElements.extras.length - 1].$extraPriceElemText);

        $extraRowElem.appendChild($extraLabelElem);
        $extraRowElem.appendChild($extraPriceElem);

        return $extraRowElem;
    }


    createTotalPayOnceAmountRow(amount = 0) {
        const $payOnceContainerElem = document.createElement('div');
        const $priceRowWithoutTaxElem = document.createElement('div');
        const $priceRowWithoutTaxLabelElem = document.createElement('span');
        const $priceRowWithoutTaxPriceElem = document.createElement('span');
        const $priceRowWithoutTaxLabelText = document.createTextNode(__('One-time amount'));

        const $taxLabelElem = document.createElement('span');
        const $taxPriceElem = document.createElement('span');

        const $totalRowElem = document.createElement('div');
        const $totalLabelElem = document.createElement('span');
        const $totalSubLabelElem = document.createElement('span');
        const $totalPriceElem = document.createElement('span');
        const $totalLabelText = document.createTextNode(__('Total'));
        const $noticeElem = document.createElement('span');
        $noticeElem.innerHTML = __('This amount is billed when the extra service is performed');

        // Add updateble elements.
        Object.defineProperties(this.receiptElements, {
            '$payOncePriceRowWithoutTaxPriceText': {
                value: document.createTextNode(moneyHelper.formatPrice(amount)),
                writable: false
            },
            '$payOnceTaxPriceText': {
                value: document.createTextNode(moneyHelper.formatPrice(moneyHelper.calculateTaxAmount(amount, this.vatPercentage))),
                writable: false
            },
            '$payOnceTotalPriceText': {
                value: document.createTextNode(moneyHelper.formatPrice(moneyHelper.calculateAmountIncludingTax(amount, this.vatPercentage))),
                writable: false
            },
            '$taxPayOnceRowElem': {
                value: document.createElement('div'),
                writable: false
            },
            '$taxPayOnceLabelText': {
                value: document.createTextNode(`${__('VAT')} (${this.vatPercentage}%)`),
                writable: false
            },
        });

        $payOnceContainerElem.classList.add('receipt__total-pay-once');

        $priceRowWithoutTaxElem.classList.add('receipt__row', 'receipt__row--total-pay-once', 'h5', 'receipt__row--heading');
        $priceRowWithoutTaxLabelElem.classList.add('receipt__col', 'receipt__col--left');
        $priceRowWithoutTaxPriceElem.classList.add('receipt__col', 'receipt__col--right', 'text-right');

        this.receiptElements.$taxPayOnceRowElem.classList.add('receipt__row', 'receipt__row--tax-pay-once');
        $taxLabelElem.classList.add('receipt__col', 'receipt__col--left');
        $taxPriceElem.classList.add('receipt__col', 'receipt__col--right', 'text-right');

        $totalRowElem.classList.add('receipt__row', 'receipt__row--total-inc-tax-pay-once', 'h5');
        $totalLabelElem.classList.add('receipt__col', 'receipt__col--left', 'receipt__label');
        $totalSubLabelElem.classList.add('receipt__col', 'receipt__col--left', 'receipt__sub-label');
        $totalPriceElem.classList.add('receipt__col', 'receipt__col--right', 'text-right', 'receipt__price');

        $noticeElem.classList.add('receipt__notice');

        // Monthly amount without tax
        $priceRowWithoutTaxLabelElem.appendChild($priceRowWithoutTaxLabelText);
        $priceRowWithoutTaxPriceElem.appendChild(this.receiptElements.$payOncePriceRowWithoutTaxPriceText);
        $priceRowWithoutTaxElem.appendChild($priceRowWithoutTaxLabelElem);
        $priceRowWithoutTaxElem.appendChild($priceRowWithoutTaxPriceElem);

        // Monthly tax amount
        $taxLabelElem.appendChild(this.receiptElements.$taxPayOnceLabelText);
        $taxPriceElem.appendChild(this.receiptElements.$payOnceTaxPriceText);
        this.receiptElements.$taxPayOnceRowElem.appendChild($taxLabelElem);
        this.receiptElements.$taxPayOnceRowElem.appendChild($taxPriceElem);

        // Monthly amount including tax
        $totalLabelElem.appendChild($totalLabelText);
        $totalLabelElem.appendChild($totalSubLabelElem);
        $totalPriceElem.appendChild(this.receiptElements.$payOnceTotalPriceText);
        $totalRowElem.appendChild($totalLabelElem);
        $totalRowElem.appendChild($totalPriceElem);

        $payOnceContainerElem.appendChild($priceRowWithoutTaxElem);
        $payOnceContainerElem.appendChild(this.receiptElements.$taxPayOnceRowElem);
        $payOnceContainerElem.appendChild($totalRowElem);
        $payOnceContainerElem.appendChild($noticeElem);

        return $payOnceContainerElem;
    }

    createTotalAmountRow(amount = 0) {
        const $monthlyContainerElem = document.createElement('div');
        const $priceRowWithoutTaxElem = document.createElement('div');
        const $priceRowWithoutTaxLabelElem = document.createElement('span');
        const $priceRowWithoutTaxPriceElem = document.createElement('span');
        const $priceRowWithoutTaxLabelText = document.createTextNode(__('Monthly costs'));
        const $taxLabelElem = document.createElement('span');
        const $taxPriceElem = document.createElement('span');

        const $totalRowElem = document.createElement('div');
        const $totalLabelElem = document.createElement('span');
        const $totalSubLabelElem = document.createElement('span');
        const $totalPriceElem = document.createElement('span');
        const $totalPriceInfo = document.createElement('span');
        const $totalLabelText = document.createTextNode(__('Total'));

        const dicountPercentage = this.state.product.contractTermDiscountPercentage ? Number(this.state.product.contractTermDiscountPercentage) : 0;
        // Add updateble elements.
        Object.defineProperties(this.receiptElements, {
            '$priceRowWithoutTaxPriceText': {
                value: document.createTextNode(moneyHelper.formatPrice(amount)),
                writable: false
            },
            '$taxPriceText': {
                value: document.createTextNode(moneyHelper.formatPrice(moneyHelper.calculateTaxAmount(amount, this.vatPercentage))),
                writable: false
            },
            '$totalPriceText': {
                value: document.createTextNode(moneyHelper.formatPrice(moneyHelper.calculateAmountIncludingTax(amount, this.vatPercentage))),
                writable: false
            },
            '$taxRowElem': {
                value: document.createElement('div'),
                writable: false
            },
            '$taxLabelText': {
                value: document.createTextNode(`${__('VAT')} (${this.vatPercentage}%)`),
                writable: false
            },
        });

        $monthlyContainerElem.classList.add('receipt__total');

        $priceRowWithoutTaxElem.classList.add('receipt__row', 'receipt__row--total', 'receipt__row--heading', 'h5');
        $priceRowWithoutTaxLabelElem.classList.add('receipt__col', 'receipt__col--left');
        $priceRowWithoutTaxPriceElem.classList.add('receipt__col', 'receipt__col--right', 'text-right');

        this.receiptElements.$taxRowElem.classList.add('receipt__row', 'receipt__row--tax');
        $taxLabelElem.classList.add('receipt__col', 'receipt__col--left');
        $taxPriceElem.classList.add('receipt__col', 'receipt__col--right', 'text-right');

        $totalRowElem.classList.add('receipt__row', 'receipt__row--total-inc-tax', 'h4');
        $totalLabelElem.classList.add('receipt__col', 'receipt__col--left', 'receipt__label');
        $totalSubLabelElem.classList.add('receipt__col', 'receipt__col--left', 'receipt__sub-label');
        $totalPriceElem.classList.add('receipt__col', 'receipt__col--right', 'text-right', 'receipt__price');

        $totalPriceInfo.classList.add('label-info');
        $totalPriceInfo.dataset.toggle = 'tooltip';
        $totalPriceInfo.dataset.tooltip = `${this.translations.total_info ? this.translations.total_info : 'Dit is de prijs die u maandelijks betaald inclusief 21% btw'}`;
        $totalPriceInfo.dataset.placement = 'top';
        $totalPriceInfo.style.fontSize = '10px';
        $totalPriceInfo.style.marginLeft = '2px';

        $totalPriceInfo.appendChild(document.createTextNode('i'));

        // Monthly amount without tax
        $priceRowWithoutTaxLabelElem.appendChild($priceRowWithoutTaxLabelText);
        $priceRowWithoutTaxPriceElem.appendChild(this.receiptElements.$priceRowWithoutTaxPriceText);
        $priceRowWithoutTaxElem.appendChild($priceRowWithoutTaxLabelElem);
        $priceRowWithoutTaxElem.appendChild($priceRowWithoutTaxPriceElem);

        // Monthly tax amount
        $taxLabelElem.appendChild(this.receiptElements.$taxLabelText);
        $taxPriceElem.appendChild(this.receiptElements.$taxPriceText);
        this.receiptElements.$taxRowElem.appendChild($taxLabelElem);
        this.receiptElements.$taxRowElem.appendChild($taxPriceElem);

        // Monthly amount including tax
        $totalLabelElem.appendChild($totalLabelText);
        $totalLabelElem.appendChild($totalSubLabelElem);
        $totalPriceElem.appendChild(this.receiptElements.$totalPriceText);
        $totalRowElem.appendChild($totalLabelElem);
        $totalRowElem.appendChild($totalPriceElem);
        $totalRowElem.appendChild($totalPriceInfo);

        if (dicountPercentage) {
            $monthlyContainerElem.appendChild(this.createDiscountRow());
        }
        $monthlyContainerElem.appendChild($priceRowWithoutTaxElem);
        $monthlyContainerElem.appendChild(this.receiptElements.$taxRowElem);
        $monthlyContainerElem.appendChild($totalRowElem);

        return $monthlyContainerElem;
    }

    createDiscountRow() {
        const dicountPercentage = this.state.product.contractTermDiscountPercentage ? Number(this.state.product.contractTermDiscountPercentage) : 0;
        if (! dicountPercentage) {
            return '';
        }
        const { amount } = this.state.product;
        const originalAmount = this.state.product.amount / (1 - dicountPercentage);
        const discountAmount = originalAmount - amount;
        const $discountPriceRowElem = document.createElement('div');
        const $discountPriceRowLabelElem = document.createElement('span');
        const $discountPriceRowPriceElem = document.createElement('span');

        $discountPriceRowLabelElem.innerHTML = __('%s% discount').replace(/%s/g, (dicountPercentage * 100));
        $discountPriceRowPriceElem.innerHTML = moneyHelper.formatPrice(discountAmount);

        $discountPriceRowElem.classList.add('receipt__row', 'receipt__row--discount');
        $discountPriceRowLabelElem.classList.add('receipt__col', 'receipt__col--left');
        $discountPriceRowPriceElem.classList.add('receipt__col', 'receipt__col--right', 'text-right');

        $discountPriceRowElem.appendChild($discountPriceRowLabelElem);
        $discountPriceRowElem.appendChild($discountPriceRowPriceElem);

        return $discountPriceRowElem;
    }

    updatePrices() {
        const dicountPercentage = this.state.product.contractTermDiscountPercentage ? Number(this.state.product.contractTermDiscountPercentage) : 0;
        const totalPrice = Amount.calculateTotalAmount(this.state);
        let totalPayOncePrice = 0;
        this.receiptElements.$priceElemText.nodeValue = moneyHelper.formatPrice(this.state.product.amount / (1 - dicountPercentage));
        this.$extraUsersHeading.classList.add('receipt__row--disabled');
        this.$modulesHeading.classList.add('receipt__row--disabled');
        this.$extrasHeading.classList.add('receipt__row--disabled');
        this.$payOnceAmountRow.classList.add('receipt__row--disabled');

        for (const user of this.receiptElements.users.filter(e => e.type.indexOf('contentProviders') === -1)) { // eslint disable no-loop-func
            const { amountPerUser, numberOfUsers, numberOfFreeUsers, chosenProvider } = this.state.users.find(_user => _user.userType === user.type);
            const extraUsers = (numberOfUsers - numberOfFreeUsers);
            user.$userPriceElemText.nodeValue = moneyHelper.formatPrice(amountPerUser * extraUsers);
            user.$numberOfUsersLabelElemText.nodeValue = extraUsers;
            user.$userPriceElemText.parentNode.parentNode.classList.toggle('receipt__row--disabled', (numberOfUsers <= numberOfFreeUsers));

            if (extraUsers > 0) {
                this.$extraUsersHeading.classList.remove('receipt__row--disabled');
            }

            const providerElement = this.receiptElements.users.filter(e => e.type === `${user.type}_contentProviders`)[0];

            if (providerElement) {
                providerElement.$userPriceElemText.parentNode.parentNode.classList.add('visually-hidden');
            }

            if (numberOfUsers && chosenProvider.length > 0) {
                const { amount, name } = chosenProvider[0];
                providerElement.$userPriceElemText.nodeValue = moneyHelper.formatPrice(amount * extraUsers);
                providerElement.$numberOfUsersLabelElemText.nodeValue = numberOfUsers;
                providerElement.$userPriceElemText.parentNode.parentNode.classList.remove('visually-hidden');
                providerElement.$userLabelElemText.nodeValue = ` x ${name}`;
            }
        }

        for (const freeUser of this.receiptElements.freeUsers) { // eslint disable no-loop-func
            const { dependency } = this.state.users.find(_user => _user.userType === freeUser.type);
            const dependentModule = this.state.modules.find(module => module.internalId === dependency);
            const dependentModuleIsActive = dependentModule ? dependentModule.active : true;

            freeUser.$userPriceElemText.parentNode.parentNode.classList.toggle('receipt__row--disabled', !dependentModuleIsActive);
        }

        for (const module of this.receiptElements.modules) { // eslint disable no-loop-func
            const { active, numberOfItems, bundlePrice } = this.state.modules.find(_module => _module.moduleType === module.type);

            if (Number(numberOfItems) !== 0 && Number(bundlePrice) !== 0) {
                const realAmount = this.calculateBundleItemsPrice(this.state.modules.find(_module => _module.moduleType === module.type));
                module.$modulePriceElemText.textContent = moneyHelper.formatPrice(realAmount);
            }

            module.$modulePriceElemText.parentNode.parentNode.classList.toggle('receipt__row--disabled', !active);

            if (active) {
                this.$modulesHeading.classList.remove('receipt__row--disabled');
            }
        }

        for (const extra of this.receiptElements.extras) { // eslint disable no-loop-func
            const { amount, active } = this.state.extras.find(_extra => _extra.extraType === extra.type);
            extra.$extraPriceElemText.parentNode.parentNode.classList.toggle('receipt__row--disabled', !active);

            if (active) {
                this.$extrasHeading.classList.remove('receipt__row--disabled');
                this.$payOnceAmountRow.classList.remove('receipt__row--disabled');
                totalPayOncePrice += Number(amount);
            }
        }

        this.receiptElements.$taxPayOnceRowElem.classList.toggle('visually-hidden', !this.showVat);
        this.receiptElements.$taxRowElem.classList.toggle('visually-hidden', !this.showVat);
        this.receiptElements.$taxLabelText.nodeValue = `${__('VAT')} (${this.vatPercentage}%)`;
        this.receiptElements.$taxPayOnceLabelText.nodeValue = `${__('VAT')} (${this.vatPercentage}%)`;
        this.receiptElements.$priceRowWithoutTaxPriceText.nodeValue = moneyHelper.formatPrice(totalPrice);
        this.receiptElements.$taxPriceText.nodeValue = moneyHelper.formatPrice(moneyHelper.calculateTaxAmount(totalPrice, this.vatPercentage));
        this.receiptElements.$totalPriceText.nodeValue = moneyHelper.formatPrice(moneyHelper.calculateAmountIncludingTax(totalPrice, this.vatPercentage));
        this.receiptElements.$payOncePriceRowWithoutTaxPriceText.nodeValue = moneyHelper.formatPrice(totalPayOncePrice);
        this.receiptElements.$payOnceTaxPriceText.nodeValue = moneyHelper.formatPrice(moneyHelper.calculateTaxAmount(totalPayOncePrice, this.vatPercentage));
        this.receiptElements.$payOnceTotalPriceText.nodeValue = moneyHelper.formatPrice(moneyHelper.calculateAmountIncludingTax(totalPayOncePrice, this.vatPercentage));
        if(this.receiptElements.$buttonPriceElemText) this.receiptElements.$buttonPriceElemText.nodeValue = moneyHelper.formatPrice(moneyHelper.calculateAmountIncludingTax(totalPrice + totalPayOncePrice, this.vatPercentage));
    }

    getCurrentStep() {
        this.state = this.store.getState();
        const current = this.state.steps.find(step => step.current === true);
        return current;
    }

    updateVat() {
        const stateCountryField = this.state.formFields.find(field => field.id === 'companyCountry');
        const stateCountryFieldValue = stateCountryField ? stateCountryField.value : false;
        const productLanguage = this.state.product.language ? this.state.product.language.split('-')[1] : COUNTRY_NETHERLANDS;
        this.country = stateCountryFieldValue || productLanguage;

        this.vatPercentage = VAT_SETTINGS[this.country] ? VAT_SETTINGS[this.country] : 0;
        this.showVat = VAT_SETTINGS[this.country] ? VAT_SETTINGS[this.country] > 0 : false;
        if (productLanguage === 'BE') {
            this.showVat = true;
            this.vatPercentage = VAT_SETTINGS[COUNTRY_BELGIUM];
        }
        if (productLanguage === 'NL' && this.country !== COUNTRY_NETHERLANDS) {
            this.showVat = false;
            this.vatPercentage = 0;
        }
    }

    update() {
        this.state = this.store.getState();
        const current = this.getCurrentStep();

        if(current.hidden) {
            this.$sidebarInner.remove();
            this.$sidebar.style.removeProperty('height');
            return;
        }

        setTimeout(() => {
            this.$sidebar.style.height = MatchMedia.notSmall() ? `${this.$sidebarInner.offsetHeight}px` : 'auto';

            this.updateSideBar();
        }, 200);

        this.updateVat();
        this.updatePrices();
    }

    render() {
        return this.$el;
    }
}

export default Receipt;
